/**
 * Keeps the property table from jumping after archive/unarchive.
 *
 * Archiving a unit can change where a row sorts on the server. If we refetch right away, the
 * row often moves (or leaves the loaded pages). Instead we update the cached list in place so
 * the row stays put, then mark the data as needing a refresh later — without fetching now.
 */

import { AccountViewListingGroupType, AccountViewPropertyUnitStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerAccountViewZrmPropertyUnitsWebApiHandler_status } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { ARCHIVE_ROW_CELL_TAGS, ARCHIVE_STALE_ONLY_TAGS } from '../../../constants/InvalidateConstants';

import type { QueryClient } from '@zg-rentals/react-query5';
import type {
  AccountViewPropertiesResponseDto,
  AccountViewPropertyResponseDto,
  AccountViewPropertyUnitsResponseDto,
  AccountViewPropertyUnitStatusDto,
  AccountViewUnitCountsDto,
  ListingAlias,
  ListingCountDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type ApplyArchiveCacheUpdatesArgs = {
  /**
   * MULTIPLEX: patch/remove the whole property row (all units). LISTING: one unit — keep the row
   * when other units remain. Same enum the modal already has; not ARCHIVE_TARGET_NOUN (UI copy).
   */
  listingType: AccountViewListingGroupType;
  groupId: string;
  listingAlias: ListingAlias;
  isUnarchiving: boolean;
};

/** `isVacantUnit` is resolved from the cache by applyArchiveCacheUpdates; omitted means unknown. */
type PropertyRowUpdateArgs = ApplyArchiveCacheUpdatesArgs & { isVacantUnit?: boolean };

type PageWithListingCount = { listingCount: ListingCountDto };

type InfinitePagesData<TPage> = {
  pages: Array<TPage>;
  pageParams?: Array<unknown>;
};

const matchesPropertyRow = (
  property: AccountViewPropertyResponseDto,
  { groupId, listingAlias }: Pick<ApplyArchiveCacheUpdatesArgs, 'groupId' | 'listingAlias'>,
): boolean => {
  if (groupId) {
    return property.groupId === groupId;
  }
  return property.listingAlias === listingAlias;
};

const clampNonNegative = (value: number): number => Math.max(0, value);

/** Moves listings between the active and archived counts used by the toggle / empty state. */
export const updateListingCount = (
  listingCount: ListingCountDto,
  { delta, isUnarchiving }: { delta: number; isUnarchiving: boolean },
): ListingCountDto => {
  if (isUnarchiving) {
    return {
      ...listingCount,
      total: listingCount.total + delta,
      archived: clampNonNegative(listingCount.archived - delta),
    };
  }

  return {
    ...listingCount,
    total: clampNonNegative(listingCount.total - delta),
    archived: listingCount.archived + delta,
  };
};

/**
 * Drops the unit that left this view from a row's counts. `vacant` only moves when we know that
 * unit was vacant, and never exceeds `total` so the card summary stays self-consistent.
 */
export const updateUnitCount = (
  unitCount: AccountViewUnitCountsDto,
  { isVacantUnit }: { isVacantUnit?: boolean },
): AccountViewUnitCountsDto => {
  const total = clampNonNegative(unitCount.total - 1);
  const vacant = isVacantUnit ? clampNonNegative(unitCount.vacant - 1) : unitCount.vacant;

  return { ...unitCount, total, vacant: Math.min(vacant, total) };
};

/**
 * Updates one page of property rows.
 * - Whole property (or last unit): remove the row.
 * - One unit in a multi-unit property: keep the row where it is and drop the unit count by 1.
 * Leaves the summary counts alone — those are applied on the first page only.
 */
export const updatePropertyTableListPage = (
  page: AccountViewPropertiesResponseDto,
  args: PropertyRowUpdateArgs,
): { page: AccountViewPropertiesResponseDto; delta: number | null } => {
  const propertyIndex = page.properties.findIndex((property) => matchesPropertyRow(property, args));
  if (propertyIndex < 0) {
    return { page, delta: null };
  }

  const property = page.properties[propertyIndex];
  const shouldRemove =
    args.listingType === AccountViewListingGroupType.MULTIPLEX ||
    (args.listingType === AccountViewListingGroupType.LISTING && property.unitCount.total <= 1);
  const delta = args.listingType === AccountViewListingGroupType.MULTIPLEX ? property.unitCount.total : 1;

  if (shouldRemove) {
    return {
      delta,
      page: {
        ...page,
        properties: page.properties.filter((_, index) => index !== propertyIndex),
      },
    };
  }

  return {
    delta,
    page: {
      ...page,
      properties: page.properties.map((row, index) => {
        if (index !== propertyIndex) {
          return row;
        }
        return {
          ...row,
          unitCount: updateUnitCount(row.unitCount, { isVacantUnit: args.isVacantUnit }),
        };
      }),
    },
  };
};

/**
 * Walks every loaded page, applies `updatePage`, and writes the listingCount change on the first
 * page (that's the only place the UI reads the summary from).
 */
const mapPagesAndUpdateListingCount = <TPage extends PageWithListingCount>(
  pages: Array<TPage>,
  {
    isUnarchiving,
    updatePage,
  }: {
    isUnarchiving: boolean;
    updatePage: (page: TPage) => { page: TPage; delta: number | null };
  },
): Array<TPage> => {
  let delta: number | null = null;
  const nextPages = pages.map((page) => {
    const result = updatePage(page);
    if (result.delta !== null) {
      delta = result.delta;
    }
    return result.page;
  });

  if (delta === null || nextPages.length === 0) {
    return pages;
  }

  const listingCountDelta = delta;

  return nextPages.map((page, index) =>
    index === 0
      ? { ...page, listingCount: updateListingCount(page.listingCount, { delta: listingCountDelta, isUnarchiving }) }
      : page,
  );
};

/** Walks property-list pages and updates first-page listingCount. */
export const updatePropertyTableListPages = (
  pages: Array<AccountViewPropertiesResponseDto>,
  args: PropertyRowUpdateArgs,
): Array<AccountViewPropertiesResponseDto> =>
  mapPagesAndUpdateListingCount(pages, {
    isUnarchiving: args.isUnarchiving,
    updatePage: (page) => updatePropertyTableListPage(page, args),
  });

/**
 * Removes one unit from a Units-view page. Does not touch listingCount - the pages walker
 * applies that on the first page.
 */
export const updatePropertyTableUnitsListPage = (
  page: AccountViewPropertyUnitsResponseDto,
  listingAlias: ListingAlias,
): { page: AccountViewPropertyUnitsResponseDto; matched: boolean } => {
  const unitIndex = page.units.findIndex((unit) => unit.listingAlias === listingAlias);
  if (unitIndex < 0) {
    return { page, matched: false };
  }

  return {
    matched: true,
    page: {
      ...page,
      units: page.units.filter((_, index) => index !== unitIndex),
    },
  };
};

/** Same as the properties-list walk, but for the flat Units view. */
export const updatePropertyTableUnitsListPages = (
  pages: Array<AccountViewPropertyUnitsResponseDto>,
  args: Pick<ApplyArchiveCacheUpdatesArgs, 'listingAlias' | 'isUnarchiving'>,
): Array<AccountViewPropertyUnitsResponseDto> =>
  mapPagesAndUpdateListingCount(pages, {
    isUnarchiving: args.isUnarchiving,
    updatePage: (page) => {
      const { page: nextPage, matched } = updatePropertyTableUnitsListPage(page, args.listingAlias);
      return { page: nextPage, delta: matched ? 1 : null };
    },
  });

const setInfinitePages = <TPage>(
  queryClient: QueryClient,
  queryKey: string,
  updatePages: (pages: Array<TPage>) => Array<TPage>,
) => {
  queryClient.setQueriesData<InfinitePagesData<TPage>>({ queryKey: [queryKey] }, (data) => {
    if (!data?.pages?.length) {
      return data;
    }

    return {
      ...data,
      pages: updatePages(data.pages),
    };
  });
};

/** The row's own status cell is the only local signal for whether the unit counted as vacant. */
const isCachedUnitVacant = (queryClient: QueryClient, listingAlias: ListingAlias) =>
  queryClient.getQueryData<AccountViewPropertyUnitStatusDto>(
    useRentalManagerAccountViewZrmPropertyUnitsWebApiHandler_status.getKey({ listingAlias }),
  )?.status === AccountViewPropertyUnitStatus.VACANT;

const hasRowCellTag = (tags: Array<string> = []) =>
  tags.some((tag) => (ARCHIVE_ROW_CELL_TAGS as ReadonlyArray<string>).includes(tag));

/**
 * Status and overflow-menu options are separate per-listing queries, so archive/unarchive leaves
 * them showing the previous state until they refetch. For LISTING, refetch cells tagged with this
 * alias and mark every other row-cell query stale (unarchive can move dupes; 30s staleTime would
 * otherwise serve old cells on remount). For MULTIPLEX, refetch all row-cell queries - unit aliases
 * under the group are unknown here.
 */
const invalidateRowCellQueries = (
  queryClient: QueryClient,
  { listingType, listingAlias }: ApplyArchiveCacheUpdatesArgs,
) => {
  // MULTIPLEX touches every unit under the group; aliases are unknown, so treat all row cells as affected.
  const isAffectedRow = (tags: Array<string> = []) =>
    listingType === AccountViewListingGroupType.MULTIPLEX || tags.includes(listingAlias);

  queryClient.invalidateQueries({
    predicate: ({ meta }) => hasRowCellTag(meta?.tags) && isAffectedRow(meta?.tags),
  });

  queryClient.invalidateQueries({
    predicate: ({ meta }) => hasRowCellTag(meta?.tags) && !isAffectedRow(meta?.tags),
    refetchType: 'none',
  });
};

/**
 * After a successful archive/unarchive: edit the lists we already have, then mark them outdated
 * without reloading. That keeps rows from jumping. Fresh data loads later when the user
 * remounts, changes filters/search, or switches views.
 */
export const applyArchiveCacheUpdates = (queryClient: QueryClient, args: ApplyArchiveCacheUpdatesArgs) => {
  // Read the status before the invalidation below marks it stale.
  const updateArgs: PropertyRowUpdateArgs = {
    ...args,
    isVacantUnit: isCachedUnitVacant(queryClient, args.listingAlias),
  };

  setInfinitePages(queryClient, 'getPropertyTableList', (pages: Array<AccountViewPropertiesResponseDto>) =>
    updatePropertyTableListPages(pages, updateArgs),
  );

  // Units view: we know which alias left, so update that list too.
  if (args.listingType === AccountViewListingGroupType.LISTING) {
    setInfinitePages(queryClient, 'getPropertyTableUnitsList', (pages: Array<AccountViewPropertyUnitsResponseDto>) =>
      updatePropertyTableUnitsListPages(pages, updateArgs),
    );
  }

  // Mark outdated but do not refetch — keep the edited order until the next natural reload.
  queryClient.invalidateQueries({
    predicate: ({ meta }) =>
      Boolean(meta?.tags?.some((tag) => (ARCHIVE_STALE_ONLY_TAGS as ReadonlyArray<string>).includes(tag))),
    refetchType: 'none',
  });

  // MULTIPLEX may drop many units; aliases are unknown, so reload the flat units list.
  if (args.listingType === AccountViewListingGroupType.MULTIPLEX) {
    queryClient.invalidateQueries({
      predicate: ({ meta }) => Boolean(meta?.tags?.includes('getPropertyTableUnitsList')),
    });
  }

  invalidateRowCellQueries(queryClient, args);
};
